import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Plus, Users, Mail, Phone, Building2, Briefcase, Edit, X, Search, Shield, LogIn, Unlock, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [userToImpersonate, setUserToImpersonate] = useState(null);
  const navigate = useNavigate();
  const { user: currentUser, impersonate } = useAuth();
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();

  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Błąd podczas pobierania użytkowników');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setValue('firstName', user.firstName || '');
    setValue('lastName', user.lastName || '');
    setValue('email', user.email || '');
    setValue('phoneNumber', user.phoneNumber || '');
    setValue('position', user.position || '');
    setValue('kadryEmail', user.kadryEmail || '');
    setValue('supervisorId', user.supervisorId ? String(user.supervisorId) : '');
    setValue('role', user.role || 'USER');
    setValue('departmentId', user.departmentId || '');
    setValue('active', user.active !== undefined ? String(user.active) : 'true');
    setValue('password', ''); // Hasło pozostaje puste - opcjonalne przy edycji
    // Ustaw uprawnienia tylko jeśli użytkownik jest Super Admin
    if (isSuperAdmin) {
      setValue('canAccessSettings', user.canAccessSettings || false);
      setValue('canAccessUsers', user.canAccessUsers || false);
      setValue('canAccessDepartments', user.canAccessDepartments || false);
      setValue('canAccessCases', user.canAccessCases || false);
      setValue('canAccessHr', user.canAccessHr || false);
      setValue('canAccessDoctorAbsences', user.canAccessDoctorAbsences || false);
      setValue('canAccessPrints', user.canAccessPrints || false);
      setValue('canAccessUmowyCru', user.canAccessUmowyCru || false);
      setValue('canAccessInvoices', user.canAccessInvoices || false);
      setValue('canAccessSubstitutions', user.canAccessSubstitutions || false);
      setValue('canAccessMfaSettings', user.canAccessMfaSettings || false);
      setValue('canAccessLeaveApprovals', user.canAccessLeaveApprovals || false);
      setValue('canAccountInvoice', user.canAccountInvoice || false);
      setValue('isManager', user.isManager === true);
      setValue('isDirector', user.isDirector === true);
      setValue('isPreviewMode', user.isPreviewMode === true);
      setValue('canCheckMerit', user.canCheckMerit || false);
      setValue('canDoPreliminaryCheck', user.canDoPreliminaryCheck || false);
      setValue('canCheckFormalAccounting', user.canCheckFormalAccounting || false);
      setValue('canAcceptInvoices', user.canAcceptInvoices || false);
      setValue('canViewSignedInvoices', user.canViewSignedInvoices || false);
      setValue('canViewAccountedInvoices', user.canViewAccountedInvoices || false);
      setValue('canDeleteDescriptionsAndSignatures', user.canDeleteDescriptionsAndSignatures || false);
      setValue('canDeleteInvoices', user.canDeleteInvoices || false);
      setValue('canEditInvoices', user.canEditInvoices || false);
      setValue('canEditPaymentDueDate', user.canEditPaymentDueDate || false);
      setValue('canUseCostCenter', user.canUseCostCenter || false);
    }
    setValue('emailOnAssignment', user.emailOnAssignment !== false);
    setValue('emailOnReturn', user.emailOnReturn !== false);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    reset();
  };

  const onEditSubmit = async (data) => {
    setSaving(true);
    try {
      const updateData = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber || null,
        position: data.position || null,
        kadryEmail: data.kadryEmail || null,
        role: data.role,
        departmentId: data.departmentId || null,
        active: data.active === 'true' || data.active === true,
      };
      
      // Dodaj hasło tylko jeśli zostało podane
      if (data.password && data.password.trim() !== '') {
        updateData.password = data.password;
      }

      // Dodaj uprawnienia tylko jeśli użytkownik jest Super Admin
      if (isSuperAdmin) {
        updateData.canAccessSettings = Boolean(data.canAccessSettings);
        updateData.canAccessUsers = Boolean(data.canAccessUsers);
        updateData.canAccessDepartments = Boolean(data.canAccessDepartments);
        updateData.canAccessCases = Boolean(data.canAccessCases);
        updateData.canAccessHr = Boolean(data.canAccessHr);
        updateData.canAccessDoctorAbsences = Boolean(data.canAccessDoctorAbsences);
        updateData.canAccessPrints = Boolean(data.canAccessPrints);
        updateData.canAccessUmowyCru = Boolean(data.canAccessUmowyCru);
        updateData.canAccessInvoices = Boolean(data.canAccessInvoices);
        updateData.canAccessSubstitutions = Boolean(data.canAccessSubstitutions);
        updateData.canAccessMfaSettings = Boolean(data.canAccessMfaSettings);
        updateData.canAccessLeaveApprovals = Boolean(data.canAccessLeaveApprovals);
        updateData.canAccountInvoice = Boolean(data.canAccountInvoice);
        // Dla checkboxów zawsze wysyłamy boolean (true/false), nie undefined
        updateData.isManager = data.isManager === true;
        updateData.isDirector = data.isDirector === true;
        updateData.isPreviewMode = data.isPreviewMode === true;
        updateData.canCheckMerit = data.canCheckMerit === true;
        updateData.canDoPreliminaryCheck = data.canDoPreliminaryCheck === true;
        updateData.canCheckFormalAccounting = data.canCheckFormalAccounting === true;
        updateData.canAcceptInvoices = data.canAcceptInvoices === true;
        updateData.canViewSignedInvoices = data.canViewSignedInvoices === true;
        updateData.canViewAccountedInvoices = data.canViewAccountedInvoices === true;
        updateData.canDeleteDescriptionsAndSignatures = data.canDeleteDescriptionsAndSignatures === true;
        updateData.canDeleteInvoices = data.canDeleteInvoices === true;
        updateData.canEditInvoices = data.canEditInvoices === true;
        updateData.canEditPaymentDueDate = data.canEditPaymentDueDate === true;
        updateData.canUseCostCenter = data.canUseCostCenter === true;
      }
      
      // Preferencje powiadomień e-mail (dla wszystkich z dostępem do użytkowników)
      updateData.emailOnAssignment = data.emailOnAssignment === true;
      updateData.emailOnReturn = data.emailOnReturn === true;
      
      console.log('Wysyłane dane:', updateData);
      await api.put(`/users/${selectedUser.id}`, updateData);
      // Przełożony zapisywany osobnym endpointem (ogólna edycja go nie zmienia).
      if (isSuperAdmin) {
        const newSup = data.supervisorId ? Number(data.supervisorId) : null;
        const oldSup = selectedUser.supervisorId || null;
        if (newSup !== oldSup) {
          try {
            await api.put(`/kadry/przelozeni/${selectedUser.id}`, { supervisorId: newSup });
          } catch (supErr) {
            toast.error(supErr.response?.data?.error || 'Nie udało się zapisać przełożonego');
          }
        }
      }
      toast.success('Użytkownik został zaktualizowany pomyślnie');
      handleCloseModal();
      fetchUsers(); // Odśwież listę
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas aktualizacji użytkownika';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlockClick = async (user) => {
    if (!window.confirm(`Odblokować konto użytkownika ${user.email}?`)) {
      return;
    }
    try {
      const response = await api.post(`/users/${user.id}/unlock`);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...response.data } : u)));
      toast.success('Konto zostało odblokowane');
    } catch (error) {
      const message = error.response?.data?.error || 'Błąd podczas odblokowywania konta';
      toast.error(message);
      console.error('Error unlocking user:', error);
    }
  };

  const handleResetTotpClick = async (user) => {
    const displayName = user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email;
    const confirmed = window.confirm(
      `Zresetować TOTP (Authenticator) dla użytkownika ${displayName}?\n\n` +
      'Użytkownik będzie musiał ponownie skonfigurować aplikację Authenticator przy następnym logowaniu — tak jak przy pierwszym logowaniu.\n\n' +
      'Użyj tej opcji, gdy użytkownik stracił telefon lub nie ma dostępu do kodów TOTP.'
    );
    if (!confirmed) {
      return;
    }
    try {
      const response = await api.post(`/users/${user.id}/reset-totp`);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...response.data } : u)));
      toast.success('TOTP zostało zresetowane — użytkownik musi skonfigurować Authenticator od nowa');
    } catch (error) {
      const message = error.response?.data?.error || 'Błąd podczas resetowania TOTP';
      toast.error(message);
      console.error('Error resetting TOTP:', error);
    }
  };

  const handleImpersonateClick = (user) => {
    setUserToImpersonate(user);
    setShowImpersonateModal(true);
  };

  const handleImpersonateConfirm = async () => {
    if (!userToImpersonate) return;
    
    try {
      const result = await impersonate(userToImpersonate.id);
      if (result.success) {
        toast.success('Zalogowano jako użytkownik');
        setShowImpersonateModal(false);
        setUserToImpersonate(null);
        navigate('/app/dashboard');
      } else {
        toast.error(result.error || 'Błąd podczas logowania jako użytkownik');
      }
    } catch (error) {
      toast.error('Błąd podczas logowania jako użytkownik');
      console.error('Error impersonating user:', error);
    }
  };

  const handleImpersonateCancel = () => {
    setShowImpersonateModal(false);
    setUserToImpersonate(null);
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      'USER': 'Użytkownik',
      'SEKRETARIAT': 'Sekretariat',
      'ADMIN': 'Administrator',
      'SUPERADMIN': 'Super Admin'
    };
    return roleLabels[role] || role;
  };

  // Funkcja generująca skróty uprawnień
  const getPermissionAbbreviations = (user) => {
    const abbreviations = [];
    
    // Dostęp do modułów
    if (user.canAccessSettings) abbreviations.push({ text: 'UST', color: 'bg-blue-100 text-blue-700 border-blue-300', title: 'Dostęp do ustawień' });
    if (user.canAccessUsers) abbreviations.push({ text: 'UŻYT', color: 'bg-indigo-100 text-indigo-700 border-indigo-300', title: 'Dostęp do użytkowników' });
    if (user.canAccessDepartments) abbreviations.push({ text: 'DZIAŁ', color: 'bg-cyan-100 text-cyan-700 border-cyan-300', title: 'Dostęp do działów' });
    if (user.canAccessCases) abbreviations.push({ text: 'SPR', color: 'bg-teal-100 text-teal-700 border-teal-300', title: 'Dostęp do spraw' });
    
    // Role organizacyjne
    if (user.isManager) abbreviations.push({ text: 'KIER', color: 'bg-purple-100 text-purple-700 border-purple-300', title: 'Kierownik działu' });
    if (user.isDirector) abbreviations.push({ text: 'DYR', color: 'bg-orange-100 text-orange-700 border-orange-300', title: 'Dyrektor' });
    if (user.isPreviewMode) abbreviations.push({ text: 'PODGL', color: 'bg-slate-100 text-slate-700 border-slate-300', title: 'Tryb podglądu' });
    
    // Uprawnienia fakturowe
    if (user.canAccountInvoice) abbreviations.push({ text: 'KSI', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', title: 'Księgowanie faktur' });
    if (user.canCheckMerit) abbreviations.push({ text: 'MER', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', title: 'Sprawdzanie merytoryczne' });
    if (user.canDoPreliminaryCheck) abbreviations.push({ text: 'WST', color: 'bg-blue-100 text-blue-700 border-blue-300', title: 'Wstępna kontrola' });
    if (user.canCheckFormalAccounting) abbreviations.push({ text: 'FR', color: 'bg-purple-100 text-purple-700 border-purple-300', title: 'Sprawdzanie formalno-rachunkowe' });
    if (user.canAcceptInvoices) abbreviations.push({ text: 'AKC', color: 'bg-red-100 text-red-700 border-red-300', title: 'Akceptacja faktur' });
    if (user.canViewSignedInvoices) abbreviations.push({ text: 'POD', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', title: 'Widok zakładki Podpisane' });
    if (user.canViewAccountedInvoices) abbreviations.push({ text: 'ZAK', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', title: 'Widok zakładki Zaksięgowane' });
    if (user.canDeleteDescriptionsAndSignatures) abbreviations.push({ text: 'USUŃ', color: 'bg-red-100 text-red-700 border-red-300', title: 'Usuwanie opisów i podpisów' });
    if (user.canDeleteInvoices) abbreviations.push({ text: 'USUŃ FAK', color: 'bg-red-200 text-red-800 border-red-400', title: 'Usuwanie faktur' });
    if (user.canEditInvoices) abbreviations.push({ text: 'EDYTUJ FAK', color: 'bg-blue-200 text-blue-800 border-blue-400', title: 'Edycja faktur' });
    if (user.canEditPaymentDueDate) abbreviations.push({ text: 'DATA PŁ', color: 'bg-rose-100 text-rose-700 border-rose-300', title: 'Edycja daty płatności' });
    if (user.canUseCostCenter) abbreviations.push({ text: 'OK', color: 'bg-amber-100 text-amber-700 border-amber-300', title: 'Ośrodek kosztów' });
    
    return abbreviations;
  };

  // Filtrowanie użytkowników na podstawie wyszukiwarki
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return users;
    }
    
    const query = searchQuery.toLowerCase().trim();
    return users.filter(user => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      const position = (user.position || '').toLowerCase();
      const department = (user.departmentName || '').toLowerCase();
      
      return fullName.includes(query) || 
             email.includes(query) || 
             position.includes(query) ||
             department.includes(query);
    });
  }, [users, searchQuery]);

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="text-xl">Lista użytkowników</h2>
        </div>
        <div className="card-body flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h2 className="text-xl">Lista użytkowników</h2>
        <button
          onClick={() => navigate('/app/users/add')}
          className="flex items-center px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Dodaj użytkownika
        </button>
      </div>
      <div className="card-body">
        {/* Wyszukiwarka */}
        {users.length > 0 && (
          <div className="mb-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Szukaj po imieniu, nazwisku, emailu, stanowisku lub dziale..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            {searchQuery && (
              <div className="mt-2 text-sm text-gray-600">
                Znaleziono {filteredUsers.length} {filteredUsers.length === 1 ? 'użytkownika' : filteredUsers.length < 5 ? 'użytkowników' : 'użytkowników'}
              </div>
            )}
          </div>
        )}

        {users.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm text-gray-900">Brak użytkowników</h3>
            <p className="mt-1 text-sm text-gray-500">Rozpocznij od dodania pierwszego użytkownika.</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/app/users/add')}
                className="inline-flex items-center px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Dodaj użytkownika
              </button>
            </div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <Search className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm text-gray-900">Nie znaleziono użytkowników</h3>
            <p className="mt-1 text-sm text-gray-500">Spróbuj zmienić kryteria wyszukiwania.</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 text-sm text-primary-600 hover:text-primary-700"
            >
              Wyczyść wyszukiwanie
            </button>
          </div>
        ) : (
          <div className="w-full overflow-x-hidden">
            <table className="w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-2 sm:px-3 md:px-4 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[18%]">
                    Imię i nazwisko
                  </th>
                  <th className="px-2 sm:px-3 md:px-4 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[20%]">
                    Email
                  </th>
                  <th className="px-2 sm:px-3 md:px-4 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell w-[10%]">
                    Telefon
                  </th>
                  <th className="px-2 sm:px-3 md:px-4 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell w-[16%]">
                    Stanowisko
                  </th>
                  <th className="px-2 sm:px-3 md:px-4 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell w-[12%]">
                    Dział
                  </th>
                  <th className="px-2 sm:px-3 md:px-4 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[10%]">
                    Rola
                  </th>
                  <th className="px-2 sm:px-3 md:px-4 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell w-[14%]">
                    Uprawnienia
                  </th>
                  <th className="px-2 sm:px-3 md:px-4 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[8%]">
                    Status
                  </th>
                  <th className="px-2 sm:px-3 md:px-4 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[8%]">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredUsers.map((user) => {
                  const permissions = getPermissionAbbreviations(user);
                  return (
                  <tr key={user.id} className="hover:bg-blue-50/30 transition-colors duration-150 border-b border-gray-100">
                    <td className="px-2 sm:px-3 md:px-4 py-3 md:py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}`
                          : <span className="break-words">{user.email}</span>}
                      </div>
                      {/* Dodatkowe informacje na małych ekranach */}
                      <div className="md:hidden mt-1 text-xs text-gray-600">
                        {user.departmentName && (
                          <div className="truncate">{user.departmentName}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 md:px-4 py-3 md:py-4">
                      <div className="text-sm text-gray-900 flex items-center min-w-0">
                        <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" />
                        <span className="break-words min-w-0">{user.email}</span>
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 md:px-4 py-3 md:py-4 whitespace-nowrap hidden md:table-cell">
                      {user.phoneNumber ? (
                        <div className="text-sm text-gray-900 flex items-center">
                          <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" />
                          {user.phoneNumber}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 sm:px-3 md:px-4 py-3 md:py-4 hidden lg:table-cell">
                      {user.position ? (
                        <div className="text-sm text-gray-900 flex items-center min-w-0">
                          <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" />
                          <span className="break-words min-w-0">{user.position}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 sm:px-3 md:px-4 py-3 md:py-4 hidden md:table-cell">
                      {user.departmentName ? (
                        <div className="text-sm text-gray-900 flex items-center min-w-0">
                          <Building2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" />
                          <span className="break-words min-w-0">{user.departmentName}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 sm:px-3 md:px-4 py-3 md:py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                        {getRoleLabel(user.role)}
                      </span>
                        {(user.isManager || user.isDirector || user.isPreviewMode) && (
                          <div className="flex gap-1 flex-wrap">
                            {user.isManager && (
                              <span className="px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                                Kierownik
                              </span>
                            )}
                            {user.isDirector && (
                              <span className="px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                                Dyrektor
                              </span>
                            )}
                            {user.isPreviewMode && (
                              <span className="px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full bg-slate-50 text-slate-700 border border-slate-200">
                                Podgląd
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 md:px-4 py-3 md:py-4 hidden lg:table-cell">
                      {permissions.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {permissions.map((perm, idx) => (
                            <span
                              key={idx}
                              className={`px-1.5 py-0.5 text-[10px] font-semibold rounded border ${perm.color}`}
                              title={perm.title}
                            >
                              {perm.text}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                      {/* Na małych ekranach pokaż uprawnienia pod rolą */}
                      <div className="lg:hidden mt-1">
                        {permissions.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {permissions.slice(0, 3).map((perm, idx) => (
                              <span
                                key={idx}
                                className={`px-1 py-0.5 text-[9px] font-semibold rounded border ${perm.color}`}
                                title={perm.title}
                              >
                                {perm.text}
                              </span>
                            ))}
                            {permissions.length > 3 && (
                              <span className="px-1 py-0.5 text-[9px] text-gray-500">+{permissions.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 md:px-4 py-3 md:py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 sm:px-3 py-1 sm:py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.active 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          {user.active ? 'Aktywny' : 'Nieaktywny'}
                        </span>
                        {user.loginLocked && (
                          <span
                            className="px-2 sm:px-3 py-1 sm:py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                            title={user.loginLockedUntil ? `Blokada do: ${user.loginLockedUntil}` : 'Blokada logowania'}
                          >
                            Zablokowany
                          </span>
                        )}
                        {user.loginMfaActive && (
                          <span
                            className="px-2 sm:px-3 py-1 sm:py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                            title="Logowanie dwuskładnikowe (TOTP) aktywne"
                          >
                            2FA
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 md:px-4 py-3 md:py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {isSuperAdmin && user.loginLocked && (
                          <button
                            onClick={() => handleUnlockClick(user)}
                            className="text-amber-600 hover:text-amber-900 flex items-center"
                            title="Odblokuj konto (blokada logowania)"
                          >
                            <Unlock className="h-4 w-4" />
                          </button>
                        )}
                        {isSuperAdmin && (user.loginMfaActive || user.totpConfigured) && (
                          <button
                            onClick={() => handleResetTotpClick(user)}
                            className="text-indigo-600 hover:text-indigo-900 flex items-center"
                            title="Zresetuj TOTP — użytkownik skonfiguruje Authenticator od nowa"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        )}
                        {isSuperAdmin && user.id !== currentUser?.id && (
                          <button
                            onClick={() => handleImpersonateClick(user)}
                            className="text-green-600 hover:text-green-900 flex items-center"
                            title="Zaloguj się jako ten użytkownik"
                          >
                            <LogIn className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEditClick(user)}
                          className="text-primary-600 hover:text-primary-900 flex items-center"
                          title="Edytuj użytkownika"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal edycji użytkownika */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edytuj użytkownika</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="editFirstName" className="block text-sm text-gray-700 mb-2">
                    Imię <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('firstName', {
                      required: 'Imię jest wymagane',
                      minLength: {
                        value: 2,
                        message: 'Imię musi mieć co najmniej 2 znaki'
                      }
                    })}
                    type="text"
                    id="editFirstName"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="editLastName" className="block text-sm text-gray-700 mb-2">
                    Nazwisko <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('lastName', {
                      required: 'Nazwisko jest wymagane',
                      minLength: {
                        value: 2,
                        message: 'Nazwisko musi mieć co najmniej 2 znaki'
                      }
                    })}
                    type="text"
                    id="editLastName"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="editEmail" className="block text-sm text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('email', {
                    required: 'Email jest wymagany',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Nieprawidłowy adres email'
                    }
                  })}
                  type="email"
                  id="editEmail"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="editPhoneNumber" className="block text-sm text-gray-700 mb-2">
                    Numer telefonu
                  </label>
                  <input
                    {...register('phoneNumber', {
                      pattern: {
                        value: /^[0-9+\-\s()]*$/,
                        message: 'Nieprawidłowy format numeru telefonu'
                      }
                    })}
                    type="tel"
                    id="editPhoneNumber"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="+48 123 456 789"
                  />
                  {errors.phoneNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.phoneNumber.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="editPosition" className="block text-sm text-gray-700 mb-2">
                    Stanowisko
                  </label>
                  <input
                    {...register('position')}
                    type="text"
                    id="editPosition"
                    list="editPositionOptions"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="np. Kierownik, Zastępca"
                  />
                  <datalist id="editPositionOptions">
                    <option value="Kierownik" />
                    <option value="Zastępca" />
                    <option value="Specjalista" />
                    <option value="Starszy Specjalista" />
                    <option value="Młodszy Specjalista" />
                    <option value="Asystent" />
                    <option value="Koordynator" />
                    <option value="Dyrektor" />
                    <option value="Wicedyrektor" />
                    <option value="Sekretarz" />
                    <option value="Pracownik" />
                  </datalist>
                </div>
              </div>

              <div>
                <label htmlFor="editKadryEmail" className="block text-sm text-gray-700 mb-2">
                  E-mail kadrowy <span className="text-gray-500 text-xs">(jak w systemie kadrowym - wiąże konto z grafikiem)</span>
                </label>
                <input
                  {...register('kadryEmail')}
                  type="email"
                  id="editKadryEmail"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="np. jan.kowalski@przyklad.pl"
                />
              </div>

              {isSuperAdmin && (
                <div>
                  <label htmlFor="editSupervisorId" className="block text-sm text-gray-700 mb-2">
                    Przełożony <span className="text-gray-500 text-xs">(podpisuje wnioski urlopowe; pusto = domyślnie kierownik działu)</span>
                  </label>
                  <select
                    {...register('supervisorId')}
                    id="editSupervisorId"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">– domyślnie (kierownik działu) –</option>
                    {users
                      .filter((u) => u.active && u.id !== selectedUser?.id)
                      .sort((a, b) => `${a.lastName || ''} ${a.firstName || ''}`.localeCompare(`${b.lastName || ''} ${b.firstName || ''}`, 'pl'))
                      .map((u) => (
                        <option key={u.id} value={String(u.id)}>
                          {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}{u.departmentName ? ` (${u.departmentName})` : ''}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="editPassword" className="block text-sm text-gray-700 mb-2">
                    Nowe hasło <span className="text-gray-500 text-xs">(opcjonalne)</span>
                  </label>
                  <input
                    {...register('password', {
                      minLength: {
                        value: 6,
                        message: 'Hasło musi mieć co najmniej 6 znaków'
                      }
                    })}
                    type="password"
                    id="editPassword"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Zostaw puste, aby nie zmieniać"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="editRole" className="block text-sm text-gray-700 mb-2">
                    Rola <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('role', {
                      required: 'Rola jest wymagana'
                    })}
                    id="editRole"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="USER">Użytkownik</option>
                    <option value="SEKRETARIAT">Sekretariat</option>
                    <option value="ADMIN">Administrator</option>
                    <option value="SUPERADMIN">Super Administrator</option>
                  </select>
                  {errors.role && (
                    <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="editDepartmentId" className="block text-sm text-gray-700 mb-2">
                    Dział
                  </label>
                  {departmentsLoading ? (
                    <div className="flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2 text-sm text-gray-500">Ładowanie działów...</span>
                    </div>
                  ) : (
                    <select
                      {...register('departmentId')}
                      id="editDepartmentId"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Brak działu</option>
                      {departments.map((department) => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label htmlFor="editActive" className="block text-sm text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    {...register('active')}
                    id="editActive"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="true">Aktywny</option>
                    <option value="false">Nieaktywny</option>
                  </select>
                </div>
              </div>

              {/* Sekcja uprawnień - tylko dla Super Admin */}
              {isSuperAdmin && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Uprawnienia dostępu</h3>
                  
                  {/* Kategoria: Dostęp do modułów */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <Shield className="h-4 w-4 mr-2 text-blue-600" />
                      Dostęp do modułów systemu
                    </h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center">
                          <input
                            {...register('canAccessSettings')}
                            type="checkbox"
                            id="editCanAccessSettings"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessSettings" className="ml-2 block text-sm text-gray-900">
                            Dostęp do ustawień
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessUsers')}
                            type="checkbox"
                            id="editCanAccessUsers"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessUsers" className="ml-2 block text-sm text-gray-900">
                            Dostęp do użytkowników
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessDepartments')}
                            type="checkbox"
                            id="editCanAccessDepartments"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessDepartments" className="ml-2 block text-sm text-gray-900">
                            Dostęp do działów
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessCases')}
                            type="checkbox"
                            id="editCanAccessCases"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessCases" className="ml-2 block text-sm text-gray-900">
                            Dostęp do spraw
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessHr')}
                            type="checkbox"
                            id="editCanAccessHr"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessHr" className="ml-2 block text-sm text-gray-900">
                            Dostęp do kadr
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessDoctorAbsences')}
                            type="checkbox"
                            id="editCanAccessDoctorAbsences"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessDoctorAbsences" className="ml-2 block text-sm text-gray-900">
                            Dostęp do nieobecności lekarzy
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessPrints')}
                            type="checkbox"
                            id="editCanAccessPrints"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessPrints" className="ml-2 block text-sm text-gray-900">
                            Dostęp do wydruków
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessInvoices')}
                            type="checkbox"
                            id="editCanAccessInvoices"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessInvoices" className="ml-2 block text-sm text-gray-900">
                            Dostęp do faktur
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessSubstitutions')}
                            type="checkbox"
                            id="editCanAccessSubstitutions"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessSubstitutions" className="ml-2 block text-sm text-gray-900">
                            Dostęp do zastępstw
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessMfaSettings')}
                            type="checkbox"
                            id="editCanAccessMfaSettings"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessMfaSettings" className="ml-2 block text-sm text-gray-900">
                            Podpis elektroniczny (pozycja w menu)
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessLeaveApprovals')}
                            type="checkbox"
                            id="editCanAccessLeaveApprovals"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessLeaveApprovals" className="ml-2 block text-sm text-gray-900">
                            Zatwierdzanie wniosków urlopowych (kadry)
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAccessUmowyCru')}
                            type="checkbox"
                            id="editCanAccessUmowyCru"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccessUmowyCru" className="ml-2 block text-sm text-gray-900">
                            Dostęp do umów CRU
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Kategoria: Role organizacyjne */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <Briefcase className="h-4 w-4 mr-2 text-purple-600" />
                      Role organizacyjne
                    </h4>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center">
                          <input
                            {...register('isManager')}
                            type="checkbox"
                            id="editIsManager"
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editIsManager" className="ml-2 block text-sm text-gray-900">
                            Kierownik działu
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('isDirector')}
                            type="checkbox"
                            id="editIsDirector"
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editIsDirector" className="ml-2 block text-sm text-gray-900">
                            Dyrektor
                          </label>
                        </div>
                        <div className="flex items-center md:col-span-2">
                          <input
                            {...register('isPreviewMode')}
                            type="checkbox"
                            id="editIsPreviewMode"
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editIsPreviewMode" className="ml-2 block text-sm text-gray-900">
                            Tryb – PODGLĄD
                            <span className="block text-xs text-gray-500 mt-0.5">
                              Czytanie i otwieranie załączników bez możliwości usuwania. Brak dostępu do ustawień, użytkowników i działów.
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Kategoria: Uprawnienia fakturowe */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-green-600" />
                      Uprawnienia fakturowe
                    </h4>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center">
                          <input
                            {...register('canAccountInvoice', { value: false })}
                            type="checkbox"
                            id="editCanAccountInvoice"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAccountInvoice" className="ml-2 block text-sm text-gray-900">
                            Księgowanie faktur
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canCheckMerit', { value: false })}
                            type="checkbox"
                            id="editCanCheckMerit"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanCheckMerit" className="ml-2 block text-sm text-gray-900">
                            Sprawdzanie merytoryczne faktur
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canDoPreliminaryCheck', { value: false })}
                            type="checkbox"
                            id="editCanDoPreliminaryCheck"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanDoPreliminaryCheck" className="ml-2 block text-sm text-gray-900">
                            Dokonano wstępnej kontroli
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canCheckFormalAccounting', { value: false })}
                            type="checkbox"
                            id="editCanCheckFormalAccounting"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanCheckFormalAccounting" className="ml-2 block text-sm text-gray-900">
                            Sprawdzanie formalno-rachunkowe faktur
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canAcceptInvoices', { value: false })}
                            type="checkbox"
                            id="editCanAcceptInvoices"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanAcceptInvoices" className="ml-2 block text-sm font-semibold text-gray-900">
                            Zgoda na akceptację faktur
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canViewSignedInvoices', { value: false })}
                            type="checkbox"
                            id="editCanViewSignedInvoices"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanViewSignedInvoices" className="ml-2 block text-sm text-gray-900">
                            Widok zakładki Podpisane
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canViewAccountedInvoices', { value: false })}
                            type="checkbox"
                            id="editCanViewAccountedInvoices"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanViewAccountedInvoices" className="ml-2 block text-sm text-gray-900">
                            Widok zakładki Zaksięgowane
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canDeleteDescriptionsAndSignatures', { value: false })}
                            type="checkbox"
                            id="editCanDeleteDescriptionsAndSignatures"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanDeleteDescriptionsAndSignatures" className="ml-2 block text-sm text-gray-900">
                            Usuwanie opisów i podpisów faktur
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canDeleteInvoices', { value: false })}
                            type="checkbox"
                            id="editCanDeleteInvoices"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanDeleteInvoices" className="ml-2 block text-sm text-gray-900">
                            Usuwanie faktur
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canEditInvoices', { value: false })}
                            type="checkbox"
                            id="editCanEditInvoices"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanEditInvoices" className="ml-2 block text-sm text-gray-900">
                            Edycja faktur
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canEditPaymentDueDate', { value: false })}
                            type="checkbox"
                            id="editCanEditPaymentDueDate"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanEditPaymentDueDate" className="ml-2 block text-sm text-gray-900">
                            Edycja daty płatności
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            {...register('canUseCostCenter', { value: false })}
                            type="checkbox"
                            id="editCanUseCostCenter"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="editCanUseCostCenter" className="ml-2 block text-sm text-gray-900">
                            Ośrodek kosztów
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Powiadomienia e-mail</h4>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      {...register('emailOnAssignment', { value: true })}
                      type="checkbox"
                      id="editEmailOnAssignment"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editEmailOnAssignment" className="ml-2 block text-sm text-gray-900">
                      Mail o otrzymaniu faktury (przekazanie)
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      {...register('emailOnReturn', { value: true })}
                      type="checkbox"
                      id="editEmailOnReturn"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editEmailOnReturn" className="ml-2 block text-sm text-gray-900">
                      Mail o zwrocie faktury
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {saving ? (
                    <span className="flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Zapisywanie...</span>
                    </span>
                  ) : (
                    'Zapisz zmiany'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia impersonacji */}
      {showImpersonateModal && userToImpersonate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-gray-200">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Potwierdzenie logowania</h3>
                <button
                  onClick={handleImpersonateCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="mb-4 sm:mb-6">
                <p className="text-sm sm:text-base text-gray-700 mb-3">
                  Czy na pewno chcesz zalogować się jako użytkownik:
                </p>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="font-semibold text-gray-900">
                    {userToImpersonate.firstName && userToImpersonate.lastName
                      ? `${userToImpersonate.firstName} ${userToImpersonate.lastName}`
                      : userToImpersonate.email}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">{userToImpersonate.email}</p>
                </div>
                <p className="text-xs sm:text-sm text-gray-500 mt-3">
                  Zostaniesz przekierowany do panelu tego użytkownika. Możesz wrócić do konta super admina w dowolnym momencie.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={handleImpersonateCancel}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleImpersonateConfirm}
                  className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  Zaloguj się
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
